const DEFAULT_MIN_HIDDEN_MS = 3000
const DEFAULT_THROTTLE_MS = 60000

/**
 * Runs `onReturn` when the tab comes back to the foreground after a genuine hidden stretch, and no more than
 * once per `throttleMs`. Returns an unsubscribe function.
 *
 * This form runs in an iframe inside workflow-hub-v2, itself an iframe inside the appNext shell. appNext
 * re-attaches its iframes on ordinary interactions, mouse movement included, and each re-attach flips this
 * document hidden→visible for a fraction of a second: a real `visibilitychange` event, not a tab switch.
 * `minHiddenMs` ignores that flicker, so only a real hidden stretch counts, and `throttleMs` keeps quick
 * switches between tabs from repeating work done moments ago. Use this rather than a bare `visibilitychange`
 * or `focus` handler for anything that should happen on a return to the tab.
 */
export function onTabRefocus(
  onReturn: () => void,
  { minHiddenMs = DEFAULT_MIN_HIDDEN_MS, throttleMs = DEFAULT_THROTTLE_MS }: { minHiddenMs?: number; throttleMs?: number } = {},
): () => void {
  // Seed from the state at attach time: `visibilitychange` never fires for the state the document was
  // already in, so a document that mounts while hidden would otherwise see its first hidden→visible
  // transition with hiddenAt still null and never run on that first reveal.
  let hiddenAt: number | null = document.visibilityState === "hidden" ? Date.now() : null
  let lastReturnAt = 0
  const handler = () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now()
      return
    }
    const now = Date.now()
    const hiddenFor = hiddenAt === null ? 0 : now - hiddenAt
    hiddenAt = null
    const throttled = now - lastReturnAt < throttleMs
    if (hiddenFor < minHiddenMs || throttled) return
    lastReturnAt = now
    onReturn()
  }
  document.addEventListener("visibilitychange", handler)
  return () => document.removeEventListener("visibilitychange", handler)
}
